/**
 * MobileClipSheet — bottom-drawer modal triggered by the iOS Share Extension
 * deep link:  tenon://clip?url=<encoded-product-url>
 *
 * The sheet loads rooms + decisions from the finish-schedule endpoint once the
 * user picks a project, then POSTs to /api/v1/mobile/clipper/clip (JWT-auth).
 */
import React, { useCallback, useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { apiGet, apiPost } from '@/lib/api'
import { useProjects } from '@/contexts/ProjectsContext'

// ── Constants ─────────────────────────────────────────────────────────────────

const SPEC_TYPES = [
  'Tile', 'Paint', 'Fixture', 'Flooring', 'Countertop',
  'Appliance', 'Lighting', 'Hardware', 'Other',
] as const

const UNITS = ['ea', 'lf', 'sf', 'cy', 'box', 'gal', 'sheet', 'bag'] as const

/** Brand fluoro used on selected spec chips + clip button — matches desktop clipper popup */
const ACCENT = '#EFF890'
const ACCENT_TEXT = '#111111'

// ── Types ─────────────────────────────────────────────────────────────────────

interface RoomGroup {
  room: string
  folder_id?: string | null
  items: Array<{ id: string; title: string }>
}

export interface MobileClipSheetProps {
  sourceUrl: string
  onClose: () => void
}

// ── Component ─────────────────────────────────────────────────────────────────

export function MobileClipSheet({ sourceUrl, onClose }: MobileClipSheetProps) {
  const { projects } = useProjects()

  // Form fields
  const [name, setName] = useState('')
  const [brand, setBrand] = useState('')
  const [price, setPrice] = useState('')
  const [sku, setSku] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [unit, setUnit] = useState<string>('ea')
  const [notes, setNotes] = useState('')
  const [specType, setSpecType] = useState<string>('')

  // Cascade selects
  const [projectId, setProjectId] = useState<string>('')
  const [roomFolderId, setRoomFolderId] = useState<string>('')
  const [targetDecisionId, setTargetDecisionId] = useState<string>('')

  // Rooms + decisions loaded after project pick
  const [rooms, setRooms] = useState<RoomGroup[]>([])
  const [roomsLoading, setRoomsLoading] = useState(false)

  // Submission state
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // ── Load rooms when project changes ─────────────────────────────────────────

  useEffect(() => {
    if (!projectId) { setRooms([]); setRoomFolderId(''); setTargetDecisionId(''); return }
    setRoomsLoading(true)
    setRoomFolderId('')
    setTargetDecisionId('')
    apiGet(`/projects/${projectId}/finish-schedule`)
      .then((data: any) => {
        const roomGroups: RoomGroup[] = (data?.rooms ?? []).map((r: any) => ({
          room: r.room,
          folder_id: r.folder_id ?? null,
          items: (r.items ?? []).map((d: any) => ({ id: d.id, title: d.title })),
        }))
        setRooms(roomGroups)
      })
      .catch(() => setRooms([]))
      .finally(() => setRoomsLoading(false))
  }, [projectId])

  // Reset decision when room changes
  useEffect(() => { setTargetDecisionId('') }, [roomFolderId])

  // ── Decisions available in the selected room ─────────────────────────────────

  const decisionsForRoom = roomFolderId
    ? (rooms.find(r => r.folder_id === roomFolderId)?.items ?? [])
    : []

  // ── Submit ───────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !projectId) return
    setSubmitting(true)
    setError(null)

    const payload: Record<string, unknown> = {
      name: name.trim(),
      brand: brand.trim(),
      price: price.trim(),
      sku: sku.trim(),
      source_url: sourceUrl,
      quantity: parseFloat(quantity) || 1,
      unit: unit || 'ea',
      notes: notes.trim(),
      photo_url: '',
      spec_sheet_url: '',
      spec_type: specType,
      project_id: parseInt(projectId, 10),
      decision_folder_id: roomFolderId || undefined,
      target_decision_id: targetDecisionId && targetDecisionId !== '__new__'
        ? targetDecisionId
        : undefined,
    }

    try {
      await apiPost('/mobile/clipper/clip', payload)
      setSuccess(true)
      setTimeout(() => onClose(), 1500)
    } catch (err: any) {
      setError(err?.message || 'Failed to clip. Please try again.')
      setSubmitting(false)
    }
  }, [name, brand, price, sku, sourceUrl, quantity, unit, notes, specType, projectId, roomFolderId, targetDecisionId, onClose])

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.55)',
        }}
      />

      {/* Sheet */}
      <div
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1001,
          background: '#FFFFFF',
          borderRadius: '16px 16px 0 0',
          boxShadow: '0 -4px 32px rgba(0,0,0,0.18)',
          maxHeight: '92dvh',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Drag handle */}
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 2 }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D0D0D0' }} />
        </div>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 16px 8px',
          borderBottom: '1px solid #EEEEEE',
        }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 16, color: '#111111' }}>
            Clip to Finish Schedule
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', padding: 4, cursor: 'pointer',
              color: '#7A7A7A', display: 'flex', alignItems: 'center',
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Source URL chip */}
        {sourceUrl && (
          <div style={{ padding: '6px 16px 0' }}>
            <span style={{
              display: 'inline-block', maxWidth: '100%',
              fontSize: 11, color: '#7A7A7A',
              background: '#F5F1E8', borderRadius: 6, padding: '3px 8px',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {sourceUrl}
            </span>
          </div>
        )}

        {success ? (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '48px 24px',
            fontFamily: 'var(--font-sans)',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
            <div style={{ fontWeight: 700, fontSize: 18, color: '#111111' }}>Clipped!</div>
            <div style={{ fontSize: 13, color: '#7A7A7A', marginTop: 4 }}>Added to your Finish Schedule</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ padding: '12px 16px 32px' }}>

            {/* ── Spec Type Chips ── */}
            <FieldLabel label="Type" />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
              {SPEC_TYPES.map(t => {
                const active = specType === t.toLowerCase()
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setSpecType(active ? '' : t.toLowerCase())}
                    style={{
                      padding: '5px 12px',
                      borderRadius: 20,
                      border: active ? `1.5px solid ${ACCENT_TEXT}` : '1.5px solid #D8D8D8',
                      background: active ? ACCENT : '#FFFFFF',
                      color: active ? ACCENT_TEXT : '#555555',
                      fontFamily: 'var(--font-sans)',
                      fontSize: 12,
                      fontWeight: active ? 600 : 400,
                      cursor: 'pointer',
                      transition: 'all 0.12s',
                    }}
                  >
                    {t}
                  </button>
                )
              })}
            </div>

            {/* ── Product Name ── */}
            <FieldLabel label="Product name" required />
            <input
              required
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. 12×24 Marble Tile"
              style={inputStyle}
            />

            {/* ── Brand ── */}
            <FieldLabel label="Brand" />
            <input
              value={brand}
              onChange={e => setBrand(e.target.value)}
              placeholder="e.g. Daltile"
              style={inputStyle}
            />

            {/* ── Price + SKU row ── */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 0 }}>
              <div style={{ flex: 1 }}>
                <FieldLabel label="Price" />
                <input
                  value={price}
                  onChange={e => setPrice(e.target.value)}
                  placeholder="0.00"
                  inputMode="decimal"
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <FieldLabel label="SKU" />
                <input
                  value={sku}
                  onChange={e => setSku(e.target.value)}
                  placeholder="Optional"
                  style={inputStyle}
                />
              </div>
            </div>

            {/* ── Quantity + Unit row ── */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 0 }}>
              <div style={{ flex: 1 }}>
                <FieldLabel label="Quantity" />
                <input
                  value={quantity}
                  onChange={e => setQuantity(e.target.value)}
                  placeholder="1"
                  inputMode="decimal"
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1 }}>
                <FieldLabel label="Unit" />
                <select
                  value={unit}
                  onChange={e => setUnit(e.target.value)}
                  style={selectStyle}
                >
                  {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>

            {/* ── Notes ── */}
            <FieldLabel label="Notes" />
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Optional notes..."
              rows={2}
              style={{ ...inputStyle, resize: 'vertical', height: 'auto', paddingTop: 8 }}
            />

            {/* ── Project ── */}
            <FieldLabel label="Project" required />
            <select
              required
              value={projectId}
              onChange={e => setProjectId(e.target.value)}
              style={selectStyle}
            >
              <option value="">Select a project…</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>

            {/* ── Room (loads after project) ── */}
            {projectId && (
              <>
                <FieldLabel label="Room" />
                <select
                  value={roomFolderId}
                  onChange={e => setRoomFolderId(e.target.value)}
                  disabled={roomsLoading}
                  style={{ ...selectStyle, opacity: roomsLoading ? 0.6 : 1 }}
                >
                  <option value="">{roomsLoading ? 'Loading rooms…' : 'No specific room'}</option>
                  {rooms.map(r => (
                    <option key={r.folder_id ?? r.room} value={r.folder_id ?? ''}>
                      {r.room}
                    </option>
                  ))}
                </select>
              </>
            )}

            {/* ── Decision (loads after room) ── */}
            {projectId && roomFolderId && (
              <>
                <FieldLabel label="Decision" />
                <select
                  value={targetDecisionId}
                  onChange={e => setTargetDecisionId(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">+ New decision</option>
                  {decisionsForRoom.map(d => (
                    <option key={d.id} value={d.id}>{d.title}</option>
                  ))}
                </select>
              </>
            )}

            {/* ── Error ── */}
            {error && (
              <div style={{
                marginTop: 8,
                padding: '8px 12px',
                background: '#FEE2E2',
                border: '1px solid #FECACA',
                borderRadius: 8,
                color: '#991B1B',
                fontFamily: 'var(--font-sans)',
                fontSize: 13,
              }}>
                {error}
              </div>
            )}

            {/* ── Submit ── */}
            <button
              type="submit"
              disabled={submitting || !name.trim() || !projectId}
              style={{
                marginTop: 20,
                width: '100%',
                padding: '14px',
                borderRadius: 10,
                border: 'none',
                background: submitting || !name.trim() || !projectId ? '#D8D8D8' : ACCENT,
                color: submitting || !name.trim() || !projectId ? '#7A7A7A' : ACCENT_TEXT,
                fontFamily: 'var(--font-sans)',
                fontSize: 15,
                fontWeight: 700,
                cursor: submitting || !name.trim() || !projectId ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s',
              }}
            >
              {submitting ? 'Clipping…' : 'Clip to Finish Schedule'}
            </button>
          </form>
        )}
      </div>
    </>
  )
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function FieldLabel({ label, required }: { label: string; required?: boolean }) {
  return (
    <div style={{ marginBottom: 4, marginTop: 12 }}>
      <span style={{
        display: 'block',
        fontFamily: 'var(--font-sans)',
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: 0.7,
        textTransform: 'uppercase',
        color: '#7A7A7A',
      }}>
        {label}{required && <span style={{ color: '#EF4444', marginLeft: 2 }}>*</span>}
      </span>
    </div>
  )
}

// ── Style constants ────────────────────────────────────────────────────────────

const sharedFieldStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1.5px solid #D8D8D8',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
  color: '#111111',
  background: '#FFFFFF',
  boxSizing: 'border-box',
  marginBottom: 0,
  outline: 'none',
  appearance: 'none',
  WebkitAppearance: 'none',
}

const inputStyle: React.CSSProperties = { ...sharedFieldStyle }
const selectStyle: React.CSSProperties = {
  ...sharedFieldStyle,
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%237A7A7A' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 12px center',
  paddingRight: 32,
}
